import React, { useState, useRef, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  ChevronDownIcon,
  LogOutIcon,
  HomeIcon,
  BookOpenIcon,
  ClipboardListIcon,
  SettingsIcon,
  TargetIcon,
  HistoryIcon,
  UsersIcon,
  MessageSquareIcon,
  BellIcon,
} from "lucide-react";
import { Avatar, AvatarFallback } from "../ui/avatar";
import { Badge } from "../ui/badge";
import { useAuth } from "../../context/AuthContext";
import { NotificationPanel } from "../notifications/NotificationPanel";
import { useNotifications } from "../../hooks/useNotifications";

export const UserProfileDropdown: React.FC = () => {
  const { user, signOut } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [isOpen, setIsOpen] = useState(false);
  const [isSigningOut, setIsSigningOut] = useState(false);
  const [showNotificationPanel, setShowNotificationPanel] = useState(false);
  const { unreadCount, updateUnreadCount } = useNotifications();
  const dropdownRef = useRef<HTMLDivElement>(null);
  const signOutTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen]);

  // Safety mechanism: Reset logout state after maximum timeout
  useEffect(() => {
    if (isSigningOut) {
      // Set a safety timeout to reset the state if something goes wrong
      signOutTimeoutRef.current = setTimeout(() => {
        setIsSigningOut(false);
      }, 60000); // 60 seconds maximum
    } else {
      // Clear the timeout if logout completes normally
      if (signOutTimeoutRef.current) {
        clearTimeout(signOutTimeoutRef.current);
        signOutTimeoutRef.current = null;
      }
    }

    return () => {
      if (signOutTimeoutRef.current) {
        clearTimeout(signOutTimeoutRef.current);
      }
    };
  }, [isSigningOut]);

  const handleNotificationClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    setShowNotificationPanel(!showNotificationPanel);
  };

  const handleCloseNotificationPanel = () => {
    setShowNotificationPanel(false);
  };

  const handleUnreadCountChange = (count: number) => {
    updateUnreadCount(count);
  };

  const handleSignOut = async () => {
    // Prevent multiple simultaneous logout attempts
    if (isSigningOut) {
      return;
    }

    try {
      setIsSigningOut(true);
      setIsOpen(false);

      // Add timeout protection at UI level as additional safety
      const timeoutPromise = new Promise<void>(
        (_, reject) => setTimeout(() => reject(new Error("UI timeout")), 35000) // 5 seconds longer than backend timeout
      );

      const logoutPromise = signOut();

      // Race between logout and UI timeout
      await Promise.race([logoutPromise, timeoutPromise]);
    } catch (error) {
      console.error("💥 Error in handleSignOut:", error);

      // Show user-friendly error message
      if (error instanceof Error && error.message.includes("UI timeout")) {
        // Don't show error toast here as the backend might still be processing
        // The AuthContext will handle the actual logout and show appropriate messages
      }
    } finally {
      // CRITICAL: Always reset the loading state, regardless of success or failure
      setIsSigningOut(false);
    }
  };

  const handleNavigationClick = (path: string) => {
    setIsOpen(false);
    navigate(path);
  };

  const toggleDropdown = () => {
    setIsOpen(!isOpen);
  };

  // Get avatar URL with fallback
  const getAvatarUrl = () => {
    if (user?.profile?.avatar_url) {
      return user.profile.avatar_url;
    }
    return "/avatars/ava1.png"; // Default to ava1.png instead of guru.png
  };

  // Navigation items - now includes Collaboration and Feedback
  const navigationItems = [
    { icon: HomeIcon, label: "Dashboard", path: "/dashboard" },
    {
      icon: BookOpenIcon,
      label: "Lesson Plan Builder",
      path: "/lesson-plan-builder",
    },
    {
      icon: ClipboardListIcon,
      label: "Assessment Builder",
      path: "/assessment-builder",
    },
    { icon: UsersIcon, label: "Collaboration", path: "/collaboration" },
    { icon: TargetIcon, label: "Point & Challenge", path: "/gamification" },
    { icon: HistoryIcon, label: "Riwayat", path: "/history" },
    { icon: SettingsIcon, label: "Pengaturan Akun", path: "/settings" },
    { icon: MessageSquareIcon, label: "Feedback", path: "#", isExternal: true },
  ];

  return (
    <div className="relative" ref={dropdownRef}>
      {/* Profile Button */}
      <button
        onClick={toggleDropdown}
        className="flex items-center gap-3.5 hover:bg-gray-50 rounded-lg p-2 transition-colors duration-200"
        disabled={isSigningOut}
      >
        <Avatar className="w-12 h-12">
          <AvatarFallback>
            <img
              className="w-full h-full object-cover"
              alt="User Avatar"
              src={getAvatarUrl()}
            />
          </AvatarFallback>
        </Avatar>

        {/* User info - Only visible on larger screens */}
        <div className="flex flex-col items-start gap-[7px] hidden sm:block">
          <div className="font-medium text-textcolor-1 text-sm whitespace-nowrap">
            {user?.profile?.name || user?.email || "Nama guru"}
          </div>
          <Badge className="bg-primarycolor-2 text-primarycolor-1 h-7 px-3 flex items-center gap-1.5 rounded-[40px] whitespace-nowrap hover:bg-primarycolor-2 hover:text-primarycolor-1 pointer-events-none">
            <img className="w-4 h-4" alt="Medal icon" src="/medal.png" />
            <span className="text-xs font-medium">
              {user?.profile?.points || 0} Point
            </span>
          </Badge>
        </div>

        <div className="w-5 h-5 flex items-center justify-center">
          {isSigningOut ? (
            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-textcolor-1"></div>
          ) : (
            <ChevronDownIcon
              className={`w-5 h-5 text-textcolor-1 transition-transform duration-200 ${
                isOpen ? "rotate-180" : ""
              }`}
            />
          )}
        </div>
      </button>

      {/* Dropdown Menu */}
      {isOpen && (
        <div className="absolute right-0 top-full mt-2 w-64 bg-white rounded-xl shadow-lg border border-gray-100 py-2 z-50 transform transition-all duration-200 ease-in-out opacity-100 scale-100">
          {/* User Profile Section - Visible on mobile in dropdown */}
          <div className="px-4 py-3 border-b border-gray-100 sm:hidden">
            <div className="flex items-center gap-3">
              <Avatar className="w-12 h-12">
                <AvatarFallback>
                  <img
                    className="w-full h-full object-cover"
                    alt="User Avatar"
                    src={getAvatarUrl()}
                  />
                </AvatarFallback>
              </Avatar>
              <div className="flex flex-col gap-2">
                <div className="font-medium text-textcolor-1 text-sm">
                  {user?.profile?.name || user?.email || "Nama guru"}
                </div>
                <Badge className="bg-primarycolor-2 text-primarycolor-1 h-7 px-3 flex items-center gap-1.5 rounded-[40px] w-fit hover:bg-primarycolor-2 hover:text-primarycolor-1 pointer-events-none">
                  <img className="w-4 h-4" alt="Medal icon" src="/medal.png" />
                  <span className="text-xs font-medium">
                    {user?.profile?.points || 0} Point
                  </span>
                </Badge>
              </div>
            </div>
          </div>

          {/* Notification Section - Mobile only */}
          <div className="px-4 py-3 border-b border-gray-100 sm:hidden">
            <div className="relative">
              <button
                onClick={handleNotificationClick}
                className="flex items-center gap-3 w-full p-2 rounded-lg hover:bg-gray-50 transition-colors duration-200"
                aria-label="Notifications"
              >
                <div className="relative w-10 h-10 bg-bgcolor-1 rounded-lg border border-[#e3e6eb] flex items-center justify-center">
                  <BellIcon className="w-5 h-5 text-textcolor-1" />

                  {/* Unread count badge */}
                  {unreadCount > 0 && (
                    <div className="absolute -top-1 -right-1 w-4 h-4 bg-red-500 rounded-full flex items-center justify-center">
                      <span className="text-white text-xs font-bold">
                        {unreadCount > 9 ? "9+" : unreadCount}
                      </span>
                    </div>
                  )}
                </div>
                <div className="flex flex-col items-start">
                  <span className="font-medium text-textcolor-1 text-sm">
                    Notifikasi
                  </span>
                  {unreadCount > 0 && (
                    <span className="text-xs text-gray-500">
                      {unreadCount} belum dibaca
                    </span>
                  )}
                </div>
              </button>

              {/* Notification Panel for Mobile */}
              <NotificationPanel
                isOpen={showNotificationPanel}
                onClose={handleCloseNotificationPanel}
                onUnreadCountChange={handleUnreadCountChange}
              />
            </div>
          </div>

          {/* Navigation Section */}
          <div>
            <div className="px-4 py-2">
              <h3 className="text-xs font-medium text-gray-500 uppercase tracking-wider">
                Navigation
              </h3>
            </div>
            {navigationItems.map(item => {
              const Icon = item.icon;
              const isActive = location.pathname === item.path;
              const isFeedback = item.label === "Feedback";

              // Handle external links (like feedback)
              if (item.isExternal) {
                return (
                  <a
                    key={item.path}
                    href={item.path}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={`w-full px-4 py-3 text-left flex items-center gap-3 transition-colors duration-150 sm:hidden ${
                      isFeedback
                        ? "text-white rounded-lg"
                        : "hover:bg-gray-50 text-textcolor-1"
                    }`}
                    style={
                      isFeedback
                        ? {
                            background:
                              "linear-gradient(93.36deg, #FF8529 0.98%, #FD7053 50%, #FA5A7D 99.02%)",
                          }
                        : {}
                    }
                  >
                    <Icon className="w-5 h-5" />
                    <span className="font-medium">{item.label}</span>
                  </a>
                );
              }

              return (
                <button
                  key={item.path}
                  onClick={() => handleNavigationClick(item.path)}
                  className={`w-full px-4 py-3 text-left flex items-center gap-3 transition-colors duration-150 ${
                    isActive
                      ? "bg-gradient-to-r from-[#6b2cfe] to-[#0217ff] text-white rounded-lg"
                      : "hover:bg-gray-50 text-textcolor-1"
                  }`}
                >
                  <Icon className="w-5 h-5" />
                  <span className="font-medium">{item.label}</span>
                </button>
              );
            })}

            {/* Divider */}
            <div className="border-t border-gray-100 my-2"></div>
          </div>

          {/* Logout Option */}
          <button
            onClick={handleSignOut}
            disabled={isSigningOut}
            className="w-full px-4 py-3 text-left flex items-center gap-3 hover:bg-red-50 transition-colors duration-150 text-red-600 disabled:opacity-50"
          >
            <LogOutIcon className="w-5 h-5" />
            <span className="font-medium">
              {isSigningOut ? "Keluar..." : "Keluar"}
            </span>
          </button>
        </div>
      )}
    </div>
  );
};
